import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

// Etappio-presentationsvideo för privatpersoner (YouTube).
const PERSON_YOUTUBE_ID = 'q3jlZg0btXM';
const SUPPORT_EMAIL = 'support@etappio.se';

interface PersonWelcomeWidgetProps {
  name?: string;
  collapsed: boolean;
  animate: boolean;
  onToggleCollapsed: (collapsed: boolean) => void;
}

/**
 * Välkomstwidget för privatpersonens startsida när inget projekt finns än.
 * Speglar byggbolagets välkomstwidget (rubrik + collapse, lazy-load-video,
 * innehåll) med privatpersonens innehåll. "Bjud in byggföretag" ligger inte
 * här utan i högerkolumnen under Notifieringar (se PersonHome), så den har
 * samma bannerdesign var man än befinner sig i flödet.
 * Collapse-tillståndet styrs av föräldern (PersonHome) och sparas som
 * `personWelcomeWidgetCollapsed` – utfälld första inloggningen, ihopfälld sen.
 */
export function PersonWelcomeWidget({
  name,
  collapsed,
  animate,
  onToggleCollapsed
}: PersonWelcomeWidgetProps) {
  const { t } = useTranslation();
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <div className="card mb-4 border-info">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="feather-smile me-2 text-info"></i>
          {t('personHome.welcomeWidget.greeting', {
            name: name || t('personHome.welcomeWidget.fallbackName', 'där')
          })}
        </h5>
        <button
          type="button"
          className="btn btn-sm btn-light-primary flex-shrink-0"
          onClick={() => onToggleCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={
            collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')
          }
          title={
            collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')
          }
        >
          <i className={`feather-chevron-${collapsed ? 'down' : 'up'}`}></i>
        </button>
      </div>
      <div
        aria-hidden={collapsed}
        style={{
          maxHeight: collapsed ? 0 : 3000,
          opacity: collapsed ? 0 : 1,
          overflow: 'hidden',
          transition: animate ? 'max-height 2s ease, opacity 2s ease' : 'none'
        }}
      >
        <div className="card-body">
          <div className="row g-4">
            <div className="col-12 col-lg-7">
              <p className="mb-3">{t('personHome.welcomeWidget.intro')}</p>

              <p className="fw-semibold mb-1">
                {t('personHome.welcomeWidget.selfSignupHeading')}
              </p>
              <p className="mb-3">{t('personHome.welcomeWidget.selfSignupBody')}</p>

              <p className="fw-semibold mb-1">
                {t('personHome.welcomeWidget.invitedHeading')}
              </p>
              <p className="mb-3">{t('personHome.welcomeWidget.invitedBody')}</p>

              <p className="mb-0 text-muted">
                <Trans
                  i18nKey="personHome.welcomeWidget.support"
                  values={{ email: SUPPORT_EMAIL }}
                  components={{
                    // Trans injects the email text as the link content at runtime.
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    a: <a href={`mailto:${SUPPORT_EMAIL}`} />
                  }}
                />
              </p>
            </div>

            <div className="col-12 col-lg-5">
              {videoPlaying ? (
                <div className="ratio ratio-16x9 rounded overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${PERSON_YOUTUBE_ID}?autoplay=1`}
                    title={t('personHome.welcomeWidget.videoTitle', 'Välkommen till Etappio')}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    style={{ border: 0 }}
                  ></iframe>
                </div>
              ) : (
                <button
                  type="button"
                  className="ratio ratio-16x9 rounded overflow-hidden border-0 p-0 position-relative w-100"
                  onClick={() => setVideoPlaying(true)}
                  aria-label={t('personHome.welcomeWidget.playVideo', 'Spela välkomstvideon')}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={`https://img.youtube.com/vi/${PERSON_YOUTUBE_ID}/hqdefault.jpg`}
                    alt={t('personHome.welcomeWidget.videoTitle', 'Välkommen till Etappio')}
                    loading="lazy"
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                  <span
                    className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff'
                    }}
                  >
                    <i className="feather-play"></i>
                  </span>
                </button>
              )}
              <small className="text-muted d-block mt-2">
                {t('personHome.welcomeWidget.aiDisclosure')}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
